import React, { useEffect, useState, useRef } from "react";
import { ym } from "../lib/dates";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, PencilSimple, Trash, Printer, FileArrowUp, Download, Stack, Bank } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import api, { formatIDR, formatApiError, formatMonth } from "../lib/api";
import { useAuth } from "../lib/auth";
import { downloadSlipPDF } from "../lib/receiptPdf";
import { generatePDF } from "../lib/pdf";

const DEFAULT_BANK = "Bank Syariah Indonesia";
const TABS = [
  { key: "list", label: "Daftar" },
  { key: "monthly", label: "Input Per Bulan" },
  { key: "rekap", label: "Rekap Per Lembaga" },
];

const emptyItems = (list) => Object.fromEntries(list.map((c) => [c.key, 0]));

export default function SlipGaji() {
  const { user } = useAuth();
  const isKaryawan = user?.role === "karyawan";
  const canManage = ["superadmin", "admin", "hr"].includes(user?.role);
  const [tab, setTab] = useState("list");
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [components, setComponents] = useState({ penerimaan: [], potongan: [] });
  const [filterPeriod, setFilterPeriod] = useState(ym());
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [monthlyForm, setMonthlyForm] = useState({ period: filterPeriod, lembaga_id: "all", rows: {} });
  const [rekap, setRekap] = useState(null);
  const fileInput = useRef();

  const load = async () => {
    const [s, e, l, c] = await Promise.all([
      isKaryawan ? api.get("/slips/me") : api.get(`/slips${filterPeriod !== "all" ? `?period=${filterPeriod}` : ""}`),
      api.get("/employees").catch(() => ({ data: [] })),
      api.get("/lembaga").catch(() => ({ data: [] })),
      api.get("/slips/components").catch(() => ({ data: { penerimaan: [], potongan: [] } })),
    ]);
    setItems(s.data);
    setEmployees(e.data);
    setLembagas(l.data);
    setComponents(c.data);
  };
  useEffect(() => { load(); }, [filterPeriod, isKaryawan]);

  const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));
  const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l.name]));

  const totalPenerimaan = (items_dict) => Object.values(items_dict || {}).reduce((a, b) => a + Number(b || 0), 0);
  const totalPotongan = (items_dict) => Object.values(items_dict || {}).reduce((a, b) => a + Number(b || 0), 0);

  // === Form ===
  const openNew = () => {
    setEditing(null);
    const first = employees[0];
    setForm({
      employee_id: first?.employee_id || "",
      period: filterPeriod === "all" ? ym() : filterPeriod,
      penerimaan_items: { ...emptyItems(components.penerimaan), gaji_pokok: first?.salary_base || 0 },
      potongan_items: emptyItems(components.potongan),
      rekening_bank: first?.rekening_bank || DEFAULT_BANK,
      rekening_no: first?.rekening_no || "",
      rekening_name: first?.rekening_name || first?.name || "",
      note: "",
    });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      employee_id: s.employee_id,
      period: s.period,
      penerimaan_items: { ...emptyItems(components.penerimaan), ...(s.penerimaan_items || {}) },
      potongan_items: { ...emptyItems(components.potongan), ...(s.potongan_items || {}) },
      rekening_bank: s.rekening_bank || DEFAULT_BANK,
      rekening_no: s.rekening_no || "",
      rekening_name: s.rekening_name || (empMap[s.employee_id]?.name || ""),
      note: s.note || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        employee_id: form.employee_id,
        period: form.period,
        penerimaan_items: Object.fromEntries(Object.entries(form.penerimaan_items || {}).map(([k, v]) => [k, Number(v || 0)])),
        potongan_items: Object.fromEntries(Object.entries(form.potongan_items || {}).map(([k, v]) => [k, Number(v || 0)])),
        rekening_bank: form.rekening_bank || DEFAULT_BANK,
        rekening_no: form.rekening_no || null,
        rekening_name: form.rekening_name || null,
        note: form.note || null,
      };
      if (editing) await api.put(`/slips/${editing.id}`, payload);
      else await api.post("/slips", payload);
      toast.success("Slip gaji tersimpan");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus slip gaji?")) return;
    try { await api.delete(`/slips/${id}`); toast.success("Terhapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  // === Excel ===
  const downloadTemplate = () => {
    const penerimaanKeys = components.penerimaan.map((c) => c.key);
    const potonganKeys = components.potongan.map((c) => c.key);
    const headers = ["employee_id", "name (info)", "period (YYYY-MM)", ...penerimaanKeys, ...potonganKeys, "rekening_bank", "rekening_no", "rekening_name", "note"];
    // Filter employees by lembaga (or all)
    const targetEmps = employees;
    const currentPeriod = filterPeriod !== "all" ? filterPeriod : ym();
    const rows = targetEmps.map((e) => [
      e.employee_id, e.name, currentPeriod,
      ...penerimaanKeys.map((k) => k === "gaji_pokok" ? (e.salary_base || 0) : 0),
      ...potonganKeys.map(() => 0),
      e.rekening_bank || "Bank Syariah Indonesia", e.rekening_no || "", e.name, "",
    ]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...(rows.length ? rows : [["EMP-100001", "Contoh", currentPeriod, ...penerimaanKeys.map(() => 0), ...potonganKeys.map(() => 0), "Bank Syariah Indonesia", "", "", ""]])]);
    // Highlight period column (col C = index 2)
    XLSX.utils.book_append_sheet(wb, ws, `Slip ${currentPeriod}`);
    XLSX.writeFile(wb, `template-slip-gaji-${currentPeriod}.xlsx`);
  };

  const importFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      const rows = data.filter((d) => d.employee_id && d.period).map((d) => {
        const penerimaan_items = {};
        components.penerimaan.forEach((c) => { if (d[c.key] !== undefined) penerimaan_items[c.key] = Number(d[c.key] || 0); });
        const potongan_items = {};
        components.potongan.forEach((c) => { if (d[c.key] !== undefined) potongan_items[c.key] = Number(d[c.key] || 0); });
        return {
          employee_id: String(d.employee_id),
          period: String(d.period),
          penerimaan_items,
          potongan_items,
          rekening_bank: d.rekening_bank ? String(d.rekening_bank) : DEFAULT_BANK,
          rekening_no: d.rekening_no ? String(d.rekening_no) : null,
          rekening_name: d.rekening_name ? String(d.rekening_name) : null,
          note: d.note ? String(d.note) : null,
        };
      });
      const { data: res } = await api.post("/slips/bulk-excel", rows);
      toast.success(`${res.inserted} baru, ${res.updated || 0} diperbarui dari ${res.total} baris`);
      if (res.errors?.length) alert(`${res.errors.length} gagal:\n` + res.errors.slice(0, 10).map((e) => `${e.employee_id}: ${e.error}`).join("\n"));
      load();
    } catch (e) { toast.error("Gagal import: " + (e.message || "")); }
  };

  // === Print single slip (A4 profesional) ===
  const printSlip = (s) => {
    const emp = empMap[s.employee_id] || {};
    const lembaga = lembagas.find((l) => l.id === emp.lembaga_id);
    downloadSlipPDF({
      slip: s, employee: emp, lembaga,
      penerimaan_defs: components.penerimaan, potongan_defs: components.potongan,
      settings: {}, period_label: formatMonth(s.period),
    });
  };
  const _oldPrintSlip = (s) => {
    const emp = empMap[s.employee_id] || {};
    const penerimaanRows = components.penerimaan.map((c) => [c.label, formatIDR(s.penerimaan_items?.[c.key] || 0)]);
    const potonganRows = components.potongan.map((c) => [c.label, formatIDR(s.potongan_items?.[c.key] || 0)]);
    generatePDF({
      title: `Slip Gaji ${formatMonth(s.period)}`,
      subtitle: emp.name || s.employee_id,
      headers: ["Uraian", "Nilai (Rp)"],
      rows: [
        ["--- PENERIMAAN ---", ""],
        ...penerimaanRows,
        ["Total Penerimaan", formatIDR(s.total_penerimaan)],
        ["--- POTONGAN ---", ""],
        ...potonganRows,
        ["Jumlah Potongan", formatIDR(s.total_potongan)],
        ["TAKE HOME PAY", formatIDR(s.take_home_pay)],
        ["Transfer ke Bank", s.rekening_bank || DEFAULT_BANK],
        ["Nomor Rekening", s.rekening_no || "-"],
        ["Atas Nama", s.rekening_name || emp.name || "-"],
      ],
      meta: {
        "Karyawan": emp.name || "-",
        "ID": s.employee_id,
        "Jabatan": emp.position || "-",
        "Lembaga": lembagaMap[emp.lembaga_id] || "-",
        "Periode": formatMonth(s.period),
      },
      filename: `slip-${s.employee_id}-${s.period}.pdf`,
    });
  };

  // === Monthly bulk input ===
  const loadMonthlyGrid = () => {
    const targetEmps = employees.filter((e) => monthlyForm.lembaga_id === "all" || e.lembaga_id === monthlyForm.lembaga_id);
    const rows = {};
    targetEmps.forEach((e) => {
      const existing = items.find((s) => s.employee_id === e.employee_id && s.period === monthlyForm.period);
      rows[e.employee_id] = existing ? {
        penerimaan_items: { ...emptyItems(components.penerimaan), ...(existing.penerimaan_items || {}) },
        potongan_items: { ...emptyItems(components.potongan), ...(existing.potongan_items || {}) },
        rekening_bank: existing.rekening_bank || e.rekening_bank || DEFAULT_BANK,
        rekening_no: existing.rekening_no || e.rekening_no || "",
        rekening_name: existing.rekening_name || e.name,
      } : {
        penerimaan_items: { ...emptyItems(components.penerimaan), gaji_pokok: e.salary_base || 0 },
        potongan_items: emptyItems(components.potongan),
        rekening_bank: e.rekening_bank || DEFAULT_BANK,
        rekening_no: e.rekening_no || "",
        rekening_name: e.name,
      };
    });
    setMonthlyForm({ ...monthlyForm, rows });
  };
  useEffect(() => { if (tab === "monthly" && employees.length && components.penerimaan.length) loadMonthlyGrid(); }, [tab, employees, components, monthlyForm.period, monthlyForm.lembaga_id]);

  const saveMonthly = async () => {
    const rows = Object.entries(monthlyForm.rows).map(([employee_id, r]) => ({
      employee_id, period: monthlyForm.period,
      penerimaan_items: r.penerimaan_items, potongan_items: r.potongan_items,
      rekening_bank: r.rekening_bank || DEFAULT_BANK, rekening_no: r.rekening_no || null, rekening_name: r.rekening_name || null,
      note: null,
    }));
    if (!rows.length) return toast.error("Tidak ada baris untuk disimpan");
    try {
      const { data: res } = await api.post("/slips/bulk-excel", rows);
      toast.success(`${res.inserted} baru, ${res.updated || 0} diperbarui`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // === Rekap ===
  const loadRekap = async () => {
    try {
      const q = new URLSearchParams({ period: filterPeriod === "all" ? ym() : filterPeriod });
      if (filterLembaga !== "all") q.set("lembaga_id", filterLembaga);
      const { data } = await api.get(`/slips/rekap?${q}`);
      setRekap(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (tab === "rekap") loadRekap(); }, [tab, filterPeriod, filterLembaga]);

  const printRekap = () => {
    if (!rekap) return;
    const lembagaLabel = filterLembaga === "all" ? "Seluruh Lembaga" : (lembagaMap[filterLembaga] || "-");
    generatePDF({
      title: `Rekap Slip Gaji ${formatMonth(rekap.period)}`,
      subtitle: lembagaLabel,
      headers: ["Karyawan", "Jabatan", "Lembaga", "Total Penerimaan", "Total Potongan", "THP", "Bank", "No Rekening"],
      rows: rekap.rows.map((r) => [r.name || r.employee_id, r.position || "-", r.lembaga_name || "-", formatIDR(r.total_penerimaan), formatIDR(r.total_potongan), formatIDR(r.take_home_pay), r.rekening_bank || "-", r.rekening_no || "-"]),
      meta: { "Periode": formatMonth(rekap.period), "Lembaga": lembagaLabel, "Total Karyawan": rekap.count, "Total THP": formatIDR(rekap.total_thp) },
      filename: `rekap-slip-${rekap.period}-${filterLembaga}.pdf`,
    });
  };
  const exportRekapExcel = () => {
    if (!rekap) return;
    const headers = ["Karyawan", "Jabatan", "Lembaga", "Total Penerimaan", "Total Potongan", "Take Home Pay", "Bank", "No Rekening", "Atas Nama"];
    const rows = rekap.rows.map((r) => [r.name || r.employee_id, r.position || "", r.lembaga_name || "", r.total_penerimaan, r.total_potongan, r.take_home_pay, r.rekening_bank || "", r.rekening_no || "", r.rekening_name || ""]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, "Rekap Slip");
    XLSX.writeFile(wb, `rekap-slip-${rekap.period}-${filterLembaga}.xlsx`);
  };

  return (
    <div data-testid="slip-gaji-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Human Resource</div>
          <h1 className="font-display text-3xl font-black text-foreground">Slip Gaji Karyawan</h1>
          <p className="text-muted-foreground text-sm mt-1">Detail komponen penerimaan & potongan, rekap per lembaga, cetak PDF/Excel.</p>
        </div>
      </div>

      {/* Tabs */}
      {!isKaryawan && (
        <div className="mb-4 flex gap-1 border-b border-border" data-testid="slip-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`slip-tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "text-[#0B8D95] border-[#0B8D95]" : "text-muted-foreground border-transparent hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* === LIST tab === */}
      {(isKaryawan || tab === "list") && (
        <>
          <div className="bg-card border border-border rounded-xl p-3 mb-3 flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">Periode</Label>
              <Input type="month" value={filterPeriod === "all" ? "" : filterPeriod} onChange={(e) => setFilterPeriod(e.target.value || "all")} className="w-40" data-testid="slip-period-filter" />
            </div>
            {canManage && (
              <div className="flex gap-2 flex-wrap ml-auto">
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="slip-template-button"><Download size={14} className="mr-1.5" />Template Excel</Button>
                <input ref={fileInput} type="file" accept=".xlsx,.xls" hidden onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} data-testid="slip-import-file" />
                <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()} data-testid="slip-import-button"><FileArrowUp size={14} className="mr-1.5" />Import Excel</Button>
                <Button size="sm" onClick={openNew} data-testid="slip-add-button"><Plus size={14} className="mr-1.5" />Buat Slip</Button>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Karyawan</th>
                    <th className="px-4 py-3 font-semibold">Periode</th>
                    <th className="px-4 py-3 font-semibold text-right">Penerimaan</th>
                    <th className="px-4 py-3 font-semibold text-right">Potongan</th>
                    <th className="px-4 py-3 font-semibold text-right">Take Home Pay</th>
                    <th className="px-4 py-3 font-semibold">Rekening</th>
                    <th className="px-4 py-3 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id} className="border-t border-border hover:bg-muted/40">
                      <td className="px-4 py-2.5 font-semibold">{empMap[s.employee_id]?.name || s.employee_id}</td>
                      <td className="px-4 py-2.5 font-mono">{formatMonth(s.period)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-600">{formatIDR(s.total_penerimaan)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-rose-600">{formatIDR(s.total_potongan)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold">{formatIDR(s.take_home_pay)}</td>
                      <td className="px-4 py-2.5 text-xs">
                        <div className="font-semibold">{s.rekening_bank || "-"}</div>
                        <div className="font-mono text-muted-foreground">{s.rekening_no || "-"}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button onClick={() => printSlip(s)} className="p-1.5 hover:bg-muted rounded" title="Cetak" data-testid={`slip-print-${s.id}`}><Printer size={14} /></button>
                        {canManage && <>
                          <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-muted rounded" data-testid={`slip-edit-${s.id}`}><PencilSimple size={14} /></button>
                          <button onClick={() => remove(s.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" data-testid={`slip-delete-${s.id}`}><Trash size={14} /></button>
                        </>}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">Belum ada slip gaji</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* === MONTHLY tab === */}
      {!isKaryawan && tab === "monthly" && canManage && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-3 border-b border-border flex flex-wrap gap-3 items-end">
            <div><Label className="text-xs">Periode</Label><Input type="month" value={monthlyForm.period} onChange={(e) => setMonthlyForm({ ...monthlyForm, period: e.target.value })} className="w-40" data-testid="monthly-period-input" /></div>
            <div><Label className="text-xs">Lembaga</Label>
              <Select value={monthlyForm.lembaga_id} onValueChange={(v) => setMonthlyForm({ ...monthlyForm, lembaga_id: v })}>
                <SelectTrigger className="w-56" data-testid="monthly-lembaga-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button className="ml-auto" onClick={saveMonthly} data-testid="monthly-save-btn"><Stack size={14} className="mr-1.5" />Simpan Semua</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted text-left uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold sticky left-0 bg-muted">Karyawan</th>
                  {components.penerimaan.map((c) => <th key={c.key} className="px-2 py-2 font-semibold text-right whitespace-nowrap">{c.label.replace("Tunjangan", "Tunj.")}</th>)}
                  {components.potongan.map((c) => <th key={c.key} className="px-2 py-2 font-semibold text-right whitespace-nowrap text-rose-600">– {c.label.replace("Ketenagakerjaan", "BPJS TK").replace("Potongan Lainnya", "Lainnya")}</th>)}
                  <th className="px-2 py-2 font-semibold text-right">THP</th>
                  <th className="px-2 py-2 font-semibold whitespace-nowrap">No Rek</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(monthlyForm.rows).map(([empId, r]) => {
                  const emp = empMap[empId] || {};
                  const thp = totalPenerimaan(r.penerimaan_items) - totalPotongan(r.potongan_items);
                  return (
                    <tr key={empId} className="border-t border-border">
                      <td className="px-3 py-2 font-semibold sticky left-0 bg-card">
                        <div>{emp.name || empId}</div>
                        <div className="text-[10px] text-muted-foreground">{lembagaMap[emp.lembaga_id] || "-"}</div>
                      </td>
                      {components.penerimaan.map((c) => (
                        <td key={c.key} className="px-1 py-1">
                          <input type="number" className="w-24 text-right font-mono text-xs border border-border rounded px-1 py-1 bg-background" value={r.penerimaan_items?.[c.key] ?? 0}
                            onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, penerimaan_items: { ...r.penerimaan_items, [c.key]: Number(e.target.value || 0) } } } })}
                            data-testid={`monthly-${empId}-${c.key}`} />
                        </td>
                      ))}
                      {components.potongan.map((c) => (
                        <td key={c.key} className="px-1 py-1">
                          <input type="number" className="w-24 text-right font-mono text-xs border border-border rounded px-1 py-1 bg-background text-rose-600" value={r.potongan_items?.[c.key] ?? 0}
                            onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, potongan_items: { ...r.potongan_items, [c.key]: Number(e.target.value || 0) } } } })}
                            data-testid={`monthly-${empId}-${c.key}`} />
                        </td>
                      ))}
                      <td className="px-2 py-2 text-right font-mono font-bold">{formatIDR(thp)}</td>
                      <td className="px-1 py-1">
                        <input type="text" className="w-32 text-xs border border-border rounded px-1 py-1 bg-background" value={r.rekening_no || ""}
                          onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, rekening_no: e.target.value } } })}
                          placeholder="BSI no rek" data-testid={`monthly-${empId}-rek`} />
                      </td>
                    </tr>
                  );
                })}
                {!Object.keys(monthlyForm.rows).length && <tr><td colSpan={components.penerimaan.length + components.potongan.length + 3} className="px-5 py-10 text-center text-muted-foreground">Tidak ada karyawan</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === REKAP tab === */}
      {!isKaryawan && tab === "rekap" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-3 border-b border-border flex flex-wrap gap-3 items-end">
            <div><Label className="text-xs">Periode</Label><Input type="month" value={filterPeriod === "all" ? ym() : filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} className="w-40" data-testid="rekap-period-input" /></div>
            <div><Label className="text-xs">Lembaga</Label>
              <Select value={filterLembaga} onValueChange={setFilterLembaga}>
                <SelectTrigger className="w-56" data-testid="rekap-lembaga-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Seluruh Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={exportRekapExcel} data-testid="rekap-excel-btn"><Download size={14} className="mr-1.5" />Excel</Button>
              <Button size="sm" onClick={printRekap} data-testid="rekap-pdf-btn"><Printer size={14} className="mr-1.5" />Cetak PDF</Button>
            </div>
          </div>
          <div className="px-4 py-3 bg-muted/40 flex flex-wrap gap-6 text-sm">
            <div><span className="text-muted-foreground">Karyawan:</span> <span className="font-bold" data-testid="rekap-count">{rekap?.count || 0}</span></div>
            <div><span className="text-muted-foreground">Total THP:</span> <span className="font-mono font-bold" data-testid="rekap-total-thp">{formatIDR(rekap?.total_thp || 0)}</span></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-4 py-2">Karyawan</th><th className="px-4 py-2">Jabatan</th><th className="px-4 py-2">Lembaga</th><th className="px-4 py-2 text-right">Penerimaan</th><th className="px-4 py-2 text-right">Potongan</th><th className="px-4 py-2 text-right">THP</th><th className="px-4 py-2">Rekening</th></tr>
              </thead>
              <tbody>
                {rekap?.rows?.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-4 py-2 font-semibold">{r.name || r.employee_id}</td>
                    <td className="px-4 py-2 text-muted-foreground">{r.position || "-"}</td>
                    <td className="px-4 py-2">{r.lembaga_name || "-"}</td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-600">{formatIDR(r.total_penerimaan)}</td>
                    <td className="px-4 py-2 text-right font-mono text-rose-600">{formatIDR(r.total_potongan)}</td>
                    <td className="px-4 py-2 text-right font-mono font-bold">{formatIDR(r.take_home_pay)}</td>
                    <td className="px-4 py-2 text-xs"><div>{r.rekening_bank || "-"}</div><div className="font-mono text-muted-foreground">{r.rekening_no || "-"}</div></td>
                  </tr>
                ))}
                {!rekap?.rows?.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === Form Dialog === */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="slip-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Slip Gaji" : "Buat Slip Gaji"}</DialogTitle>
            <DialogDescription>Lengkapi seluruh komponen penerimaan & potongan. Take Home Pay dihitung otomatis.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2">
              <Label>Karyawan *</Label>
              <Select value={form.employee_id} onValueChange={(v) => {
                const emp = employees.find((e) => e.employee_id === v);
                setForm({ ...form, employee_id: v, rekening_name: emp?.name || form.rekening_name, rekening_bank: emp?.rekening_bank || form.rekening_bank || DEFAULT_BANK, rekening_no: emp?.rekening_no || form.rekening_no });
              }}>
                <SelectTrigger data-testid="slip-employee-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Periode (Bulan) *</Label><Input type="month" value={form.period || ""} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="slip-period-input" /></div>
            <div></div>
          </div>

          <div className="mb-3">
            <div className="text-xs uppercase tracking-widest font-bold text-emerald-700 mb-2">Penerimaan</div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {components.penerimaan.map((c) => (
                <div key={c.key}>
                  <Label className="text-xs">{c.label}</Label>
                  <Input type="number" value={form.penerimaan_items?.[c.key] ?? 0}
                    onChange={(e) => setForm({ ...form, penerimaan_items: { ...form.penerimaan_items, [c.key]: e.target.value } })}
                    data-testid={`slip-p-${c.key}`} />
                </div>
              ))}
            </div>
            <div className="mt-2 text-right text-sm">Total Penerimaan: <span className="font-mono font-bold text-emerald-600" data-testid="slip-total-penerimaan">{formatIDR(totalPenerimaan(form.penerimaan_items))}</span></div>
          </div>

          <div className="mb-3">
            <div className="text-xs uppercase tracking-widest font-bold text-rose-700 mb-2">Potongan</div>
            <div className="grid grid-cols-2 gap-2">
              {components.potongan.map((c) => (
                <div key={c.key}>
                  <Label className="text-xs">{c.label}</Label>
                  <Input type="number" value={form.potongan_items?.[c.key] ?? 0}
                    onChange={(e) => setForm({ ...form, potongan_items: { ...form.potongan_items, [c.key]: e.target.value } })}
                    data-testid={`slip-d-${c.key}`} />
                </div>
              ))}
            </div>
            <div className="mt-2 text-right text-sm">Jumlah Potongan: <span className="font-mono font-bold text-rose-600" data-testid="slip-total-potongan">{formatIDR(totalPotongan(form.potongan_items))}</span></div>
          </div>

          <div className="p-3 bg-[#0B8D95]/8 border border-[#0B8D95]/20 rounded-lg mb-3 flex items-center justify-between">
            <div className="text-sm font-semibold text-[#0B8D95]">TAKE HOME PAY</div>
            <div className="font-mono text-2xl font-bold text-[#0B8D95]" data-testid="slip-thp">{formatIDR(totalPenerimaan(form.penerimaan_items) - totalPotongan(form.potongan_items))}</div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-3">
            <div><Label className="text-xs flex items-center gap-1"><Bank size={13} />Transfer ke Bank</Label><Input value={form.rekening_bank || DEFAULT_BANK} onChange={(e) => setForm({ ...form, rekening_bank: e.target.value })} data-testid="slip-rek-bank" /></div>
            <div><Label className="text-xs">Nomor Rekening</Label><Input value={form.rekening_no || ""} onChange={(e) => setForm({ ...form, rekening_no: e.target.value })} data-testid="slip-rek-no" /></div>
            <div><Label className="text-xs">Atas Nama</Label><Input value={form.rekening_name || ""} onChange={(e) => setForm({ ...form, rekening_name: e.target.value })} data-testid="slip-rek-name" /></div>
          </div>

          <div><Label className="text-xs">Catatan</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="slip-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
